// SkuAvailabilityCard — 設定 > 品番マスター > 空き番号管理
// 番号状態（予約 / 発行済 / 解放 / 再利用可）・カウンター・操作履歴を1画面に統合。
// 自動再利用は廃止 — 解放 → 再利用可 → 管理者が発番時に選択、という明示フローのみ。
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ListChecks, ArrowsClockwise, MapTrifold, ArrowCounterClockwise, Warning, FileXls } from '@phosphor-icons/react';
import { formatJST } from '@/utils/datetime';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import SkuCountersCard from './SkuCountersCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sku`;

const DIVISIONS = [
  { code: 'all', label: 'すべて' }, { code: 'seizo', label: '製造' }, { code: 'shizai', label: '資材' },
  { code: 'it_assets', label: 'IT資産' }, { code: 'office_equipment', label: 'オフィス備品' }, { code: 'other', label: 'その他' },
];
const STATUS = {
  used: { label: '発行済', chip: 'bg-emerald-100 text-emerald-800 border-emerald-300', cell: 'bg-emerald-500 text-white border-emerald-600' },
  issued: { label: '予約', chip: 'bg-sky-100 text-sky-800 border-sky-300', cell: 'bg-sky-400 text-white border-sky-500' },
  released: { label: '解放', chip: 'bg-amber-50 text-amber-700 border-amber-300', cell: 'bg-amber-200 text-amber-800 border-amber-400' },
  reusable: { label: '再利用可', chip: 'bg-violet-100 text-violet-700 border-violet-300', cell: 'bg-violet-300 text-violet-900 border-violet-400' },
  gap: { label: '空き番号', chip: 'bg-zinc-100 text-zinc-500 border-zinc-200', cell: 'bg-white text-zinc-400 border-zinc-200' },
  conflict: { label: '競合', chip: 'bg-rose-100 text-rose-800 border-rose-300', cell: 'bg-rose-400 text-white border-rose-500' },
};
const STATUS_TABS = [
  ['all', 'すべて'], ['used', '発行済'], ['issued', '予約'], ['released', '解放'],
  ['reusable', '再利用可'], ['gap', '空き番号'], ['conflict', '競合'],
];

export default function SkuAvailabilityCard() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [templates, setTemplates] = useState([]);
  const [division, setDivision] = useState('all');
  const [tplId, setTplId] = useState('');
  const [maps, setMaps] = useState([]);          // [{template, buckets...}]
  const [reclaimedQ, setReclaimedQ] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusTab, setStatusTab] = useState('all');
  const [q, setQ] = useState('');
  const [view, setView] = useState('list');       // list | strip | map
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resolveTargets, setResolveTargets] = useState(null);
  const [rangeStart, setRangeStart] = useState(0);

  useEffect(() => {
    axios.get(`${API}/templates`, { withCredentials: true })
      .then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const visibleTpls = useMemo(() => templates.filter((t) =>
    division === 'all' || (t.app_code || '') === division || (t.app_code || '') === ''), [templates, division]);

  const load = useCallback(async () => {
    const targets = tplId ? visibleTpls.filter((t) => t.id === tplId) : visibleTpls;
    if (targets.length === 0) { setMaps([]); return; }
    setBusy(true);
    try {
      const [results, rq] = await Promise.all([
        Promise.all(targets.slice(0, 20).map((t) =>
          axios.get(`${API}/map`, { params: { template_id: t.id }, withCredentials: true })
            .then((r) => r.data).catch(() => null))),
        axios.get(`${API}/reclaimed`, { withCredentials: true }).then((r) => r.data?.items || []).catch(() => []),
      ]);
      setMaps(results.filter(Boolean));
      setReclaimedQ(rq || []);
      setSelected(new Set());
      setRangeStart(0);
    } finally { setBusy(false); }
  }, [tplId, visibleTpls]);
  useEffect(() => { load(); }, [load]);

  const stateBySku = useMemo(() => {
    const m = new Map();
    (reclaimedQ || []).forEach((r) => m.set(r.sku, r.state || 'reusable'));
    return m;
  }, [reclaimedQ]);

  // 統合行リスト
  const rows = useMemo(() => {
    const out = [];
    for (const m of maps) {
      for (const b of m.buckets || []) {
        const conflictBy = new Map((b.conflicts || []).map((c) => [c.serial, c]));
        for (const u of b.used || []) {
          out.push({
            key: `${m.template.id}:${u.sku}`, status: u.linked ? 'used' : 'issued',
            sku: u.sku, serial: u.serial, template: m.template, bucket: b.bucket,
            linked_name: u.linked_name, linked_type: u.linked_type,
            issued_by: u.issued_by, issued_at: u.issued_at,
          });
        }
        for (const g of b.gaps_detail || []) {
          const cf = conflictBy.get(g.serial);
          const st = stateBySku.get(g.sku);
          out.push({
            key: `${m.template.id}:${g.sku}`,
            status: cf ? 'conflict' : (st || 'gap'),
            sku: g.sku, serial: g.serial, template: m.template, bucket: b.bucket,
            owner_type: cf?.owner_type, owner_name: cf?.owner_name,
          });
        }
      }
    }
    out.sort((a, b2) => (a.sku || '').localeCompare(b2.sku || ''));
    return out;
  }, [maps, stateBySku]);

  const summary = useMemo(() => ({
    used: rows.filter((r) => r.status === 'used').length,
    issued: rows.filter((r) => r.status === 'issued').length,
    released: rows.filter((r) => r.status === 'released').length,
    reusable: rows.filter((r) => r.status === 'reusable').length,
    gap: rows.filter((r) => r.status === 'gap').length,
    conflict: rows.filter((r) => r.status === 'conflict').length,
    next: maps.length === 1 && maps[0].buckets?.length
      ? String(maps[0].buckets[0].next_planned).padStart(maps[0].counter_width || 4, '0')
      : null,
  }), [rows, maps]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows
      .filter((r) => statusTab === 'all' || r.status === statusTab)
      .filter((r) => !s || [r.sku, r.template?.name, r.template?.code, r.bucket, r.linked_name, r.issued_by]
        .filter(Boolean).join(' ').toLowerCase().includes(s));
  }, [rows, statusTab, q]);

  const selectableRows = filtered.filter((r) => r.status === 'issued');
  const releasedRows = filtered.filter((r) => r.status === 'released');
  const reusableRows = filtered.filter((r) => r.status === 'reusable');
  const conflictRows = filtered.filter((r) => r.status === 'conflict');
  const toggleAll = () => {
    setSelected((prev) => prev.size === selectableRows.length && selectableRows.length > 0
      ? new Set() : new Set(selectableRows.map((r) => r.sku)));
  };

  const doRelease = async () => {
    setConfirmOpen(false);
    try {
      const cand = await axios.get(`${API}/reclaimable`, { params: { limit: 1000 }, withCredentials: true }).then((r) => r.data);
      const ids = (cand.items || cand || []).filter((c) => selected.has(c.sku)).map((c) => c.id);
      if (ids.length === 0) { toast.error('選択した番号は解放できません（既に紐付いている可能性があります）'); return; }
      const res = await axios.post(`${API}/numbers/release`, { ids }, { withCredentials: true }).then((r) => r.data);
      toast.success(`${res.released ?? ids.length}件を解放しました — 自動では再利用されません`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '解放に失敗しました');
    }
  };

  const approveReuse = async (skus) => {
    try {
      const res = await axios.post(`${API}/numbers/approve-reuse`, { skus }, { withCredentials: true }).then((r) => r.data);
      toast.success(`${res.updated}件を再利用可にしました — 発番時に管理者が選択できます`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '再利用可への変更に失敗しました'); }
  };

  const revokeState = async (skus) => {
    try {
      const res = await axios.post(`${API}/numbers/revoke`, { skus }, { withCredentials: true }).then((r) => r.data);
      toast.success(`${res.updated}件を予約に戻しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '取消に失敗しました'); }
  };

  const doResolve = async () => {
    const targets = resolveTargets || [];
    setResolveTargets(null);
    try {
      const byTpl = {};
      targets.forEach((r) => { (byTpl[r.template.id] = byTpl[r.template.id] || []).push({ sku: r.sku, bucket: r.bucket || '' }); });
      let total = 0;
      for (const [tid, items] of Object.entries(byTpl)) {
        const res = await axios.post(`${API}/conflicts/resolve`, { template_id: tid, items }, { withCredentials: true }).then((r) => r.data);
        total += res.resolved || 0;
      }
      toast.success(`${total}件の競合を解消しました — 発番済みとして正式登録されました`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '競合の解消に失敗しました');
    }
  };

  const shown = filtered.slice(0, 300);
  const isNumberView = view !== 'history' && view !== 'counters';

  return (
    <Card className="p-5 space-y-4" data-testid="sku-avail-card">
      <div className="flex items-center gap-2 flex-wrap">
        <ListChecks size={20} weight="duotone" className="text-emerald-600" />
        <div>
          <h3 className="font-bold text-zinc-800">空き番号管理</h3>
          <p className="text-[11px] text-zinc-400">
            番号状態（予約 / 発行済 / 解放 / 再利用可）とカウンター・操作履歴。発番は常に次の連番。解放番号は管理者が明示的に選んだときだけ再利用されます
          </p>
        </div>
        <button type="button" onClick={load} className="ml-auto text-zinc-400 hover:text-emerald-600" title="更新" data-testid="sku-avail-reload">
          <ArrowsClockwise size={16} />
        </button>
        <button type="button" onClick={async () => {
          try {
            const params = tplId ? { template_id: tplId } : {};
            const res = await axios.get(`${API}/availability/export`, { params, responseType: 'blob', withCredentials: true });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const disp = res.headers?.['content-disposition'] || '';
            const m = /filename="([^"]+)"/.exec(disp);
            a.download = m ? m[1] : `sku_availability_${Date.now()}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success('SKU空き状況をExcelでエクスポートしました');
          } catch (e) {
            toast.error('Excelエクスポートに失敗しました');
          }
        }} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
        title="現在のフィルタ条件でExcelエクスポート" data-testid="sku-avail-export-xlsx">
          <FileXls size={13} weight="fill" /> Excel出力
        </button>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2" data-testid="sku-avail-summary">
        {[['used', summary.used, 'text-emerald-700 bg-emerald-50 border-emerald-200'],
          ['issued', summary.issued, 'text-sky-700 bg-sky-50 border-sky-200'],
          ['released', summary.released, 'text-amber-700 bg-amber-50 border-amber-200'],
          ['reusable', summary.reusable, 'text-violet-700 bg-violet-50 border-violet-200'],
          ['conflict', summary.conflict, 'text-rose-700 bg-rose-50 border-rose-200']].map(([k, v, cls]) => (
          <div key={k} className={`rounded-xl border px-3 py-2 ${cls}`} data-testid={`sku-avail-sum-${k}`}>
            <p className="text-[10px] font-semibold opacity-70">{STATUS[k].label}</p>
            <p className="text-xl font-bold">{v}</p>
          </div>
        ))}
        <div className="rounded-xl border px-3 py-2 text-sky-700 bg-sky-50 border-sky-200" data-testid="sku-avail-sum-next">
          <p className="text-[10px] font-semibold opacity-70">次回発番候補</p>
          <p className="text-sm font-bold font-mono mt-1">{summary.next || (tplId ? '—' : 'ルールを選択')}</p>
        </div>
      </div>

      {/* フィルター */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {DIVISIONS.map((d) => (
          <button key={d.code} type="button" onClick={() => { setDivision(d.code); setTplId(''); }}
            data-testid={`sku-avail-div-${d.code}`}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
              division === d.code ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50'}`}>
            {d.label}
          </button>
        ))}
        <select value={tplId} onChange={(e) => setTplId(e.target.value)}
          className="h-8 rounded-lg border border-zinc-200 bg-white px-2 text-xs text-zinc-700 outline-none"
          data-testid="sku-avail-tpl-select">
          <option value="">全ルール</option>
          {visibleTpls.map((t) => <option key={t.id} value={t.id}>{t.name}{t.sample ? `（${t.sample}）` : ''}</option>)}
        </select>
        <div className="flex rounded-lg border border-zinc-200 overflow-hidden ml-auto" data-testid="sku-avail-view-toggle">
          {[['list', '一覧'], ['strip', '簡易マップ'], ['map', '詳細マップ'], ['counters', 'カウンター'], ['history', '操作履歴']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setView(v)}
              data-testid={`sku-avail-view-${v}`}
              className={`px-2.5 py-1.5 text-[11px] font-semibold ${view === v ? 'bg-sky-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {isNumberView && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_TABS.map(([v, label]) => (            <button key={v} type="button" onClick={() => setStatusTab(v)}
              data-testid={`sku-avail-status-${v}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                statusTab === v ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-sky-50'}`}>
              {label}
            </button>
          ))}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="SKU / ルール / 品目・資産名で検索..."
            className="h-8 text-xs flex-1 min-w-[200px]" data-testid="sku-avail-search" />
        </div>
      )}

      {/* 凡例 */}
      {isNumberView && (
        <div className="flex items-center gap-3 flex-wrap text-[10px] text-zinc-500" data-testid="sku-avail-legend">
          {Object.entries(STATUS).map(([k, s]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={`inline-block h-3 w-3 rounded border ${s.cell}`} /> {s.label}
            </span>
          ))}
        </div>
      )}

      {busy && isNumberView && <p className="py-8 text-center text-sm text-zinc-400">読み込み中...</p>}
      {!busy && isNumberView && maps.length === 0 && <p className="py-8 text-center text-sm text-zinc-400">この区分の品番ルールにはまだ発番データがありません</p>}

      {/* 簡易マップストリップ（一覧・簡易マップ表示時） */}
      {!busy && (view === 'list' || view === 'strip') && tplId && maps.length === 1 && (
        <CompactStrip map={maps[0]} stateBySku={stateBySku} rangeStart={rangeStart} setRangeStart={setRangeStart} full={view === 'strip'} />
      )}

      {/* 一覧（デフォルト） */}
      {!busy && view === 'list' && maps.length > 0 && (
        <div className="space-y-2">
          {admin && (selectableRows.length > 0 || releasedRows.length > 0 || reusableRows.length > 0 || conflictRows.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              {selectableRows.length > 0 && (
                <>
                  <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-[11px]" data-testid="sku-avail-select-all">
                    すべて選択（{selectableRows.length}）
                  </Button>
                  <Button size="sm" disabled={selected.size === 0} onClick={() => setConfirmOpen(true)}
                    className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700" data-testid="sku-avail-release-btn">
                    選択した番号を解放（{selected.size}）
                  </Button>
                </>
              )}
              {releasedRows.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => approveReuse(releasedRows.map((r) => r.sku))}
                  className="h-7 text-[11px] border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                  data-testid="sku-avail-approve-all">
                  解放中をまとめて再利用可にする（{releasedRows.length}）
                </Button>
              )}
              {reusableRows.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => revokeState(reusableRows.map((r) => r.sku))}
                  className="h-7 text-[11px]" data-testid="sku-avail-revoke-all">
                  <ArrowCounterClockwise size={12} className="mr-1" /> 再利用可を予約に戻す（{reusableRows.length}）
                </Button>
              )}
              {conflictRows.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => setResolveTargets(conflictRows)}
                  className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                  data-testid="sku-avail-resolve-all">
                  <Warning size={12} className="mr-1" /> 競合をまとめて解消（{conflictRows.length}）
                </Button>
              )}
              <span className="text-[10px] text-zinc-400">※ 解放できるのは「予約（未紐付け）」のみ。発番は常に次の連番で、解放番号が自動で使われることはありません</span>
            </div>
          )}
          {!admin && (
            <p className="text-[10px] text-zinc-400" data-testid="sku-avail-readonly-note">
              番号状態の変更（解放 / 再利用可 / 取消）は管理者のみ実行できます（閲覧は可能です）
            </p>
          )}
          <div className="rounded-xl border border-zinc-200 overflow-x-auto">
            <table className="w-full text-xs" data-testid="sku-avail-table">
              <thead className="bg-zinc-100 text-zinc-700 text-[10px]">
                <tr>
                  <th className="px-2 py-2 w-8" />
                  <th className="text-left px-2 py-2 font-semibold">状態</th>
                  <th className="text-left px-2 py-2 font-semibold">SKU</th>
                  <th className="text-left px-2 py-2 font-semibold hidden md:table-cell">ルール</th>
                  <th className="text-left px-2 py-2 font-semibold hidden lg:table-cell">Bucket</th>
                  <th className="text-left px-2 py-2 font-semibold">紐付け先</th>
                  <th className="text-left px-2 py-2 font-semibold hidden lg:table-cell">発番者</th>
                  <th className="text-left px-2 py-2 font-semibold hidden lg:table-cell">発番日</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.key} className="border-t border-zinc-200" data-testid={`sku-avail-row-${r.sku}`}>
                    <td className="px-2 py-1.5 text-center">
                      {r.status === 'issued' && admin && (
                        <input type="checkbox" checked={selected.has(r.sku)}
                          onChange={() => setSelected((p) => { const n = new Set(p); n.has(r.sku) ? n.delete(r.sku) : n.add(r.sku); return n; })}
                          className="accent-amber-600" data-testid={`sku-avail-check-${r.sku}`} />
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold whitespace-nowrap ${STATUS[r.status].chip}`}>
                        {STATUS[r.status].label}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono font-semibold text-zinc-800">{r.sku}</td>
                    <td className="px-2 py-1.5 text-zinc-500 hidden md:table-cell">{r.template?.name}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-400 hidden lg:table-cell">{r.bucket}</td>
                    <td className="px-2 py-1.5 text-zinc-600">
                      {r.status === 'used'
                        ? <span>{r.linked_type && <span className="text-[10px] text-zinc-400 mr-1">[{r.linked_type}]</span>}{r.linked_name || '—'}</span>
                        : r.status === 'issued' ? <span className="text-sky-500">未紐付け（予約中）</span>
                          : r.status === 'released' ? <span className="text-amber-600">解放済み — 承認待ち</span>
                            : r.status === 'reusable' ? <span className="text-violet-600">再利用可（管理者が選択可能）</span>
                              : r.status === 'conflict' ? (
                              <span className="text-rose-700 font-semibold">
                                {r.owner_type && <span className="text-[10px] font-normal text-rose-500 mr-1">[{r.owner_type}]</span>}
                                {r.owner_name || '手動使用'} — 未発番のまま使用中
                              </span>
                            )
                                : <span className="text-zinc-300">未発番</span>}
                    </td>
                    <td className="px-2 py-1.5 text-zinc-500 hidden lg:table-cell">{r.issued_by || ''}</td>
                    <td className="px-2 py-1.5 text-zinc-400 hidden lg:table-cell">{(r.issued_at || '').slice(0, 10)}</td>
                    <td className="px-2 py-1.5 text-right">
                      {r.status === 'released' && admin && (
                        <button type="button" onClick={() => approveReuse([r.sku])}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-violet-300 bg-violet-50 text-[10px] font-semibold text-violet-700 hover:bg-violet-100"
                          title="再利用可にする（発番時に管理者が選択できます）" data-testid={`sku-avail-approve-${r.sku}`}>
                          再利用可にする
                        </button>
                      )}
                      {(r.status === 'released' || r.status === 'reusable') && admin && (
                        <button type="button" onClick={() => revokeState([r.sku])}
                          className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-zinc-400 hover:text-sky-600"
                          title="予約に戻す（取消）" data-testid={`sku-avail-revoke-${r.sku}`}>
                          <ArrowCounterClockwise size={12} /> 取消
                        </button>
                      )}
                      {r.status === 'conflict' && admin && (
                        <button type="button" onClick={() => setResolveTargets([r])}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-rose-300 bg-rose-50 text-[10px] font-semibold text-rose-700 hover:bg-rose-100"
                          title="発行済みとして正式登録し、二重発番を防ぎます" data-testid={`sku-avail-resolve-${r.sku}`}>
                          <Warning size={11} /> 解消
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 300 && (
              <p className="px-3 py-2 text-[10px] text-zinc-400 border-t border-zinc-200">{filtered.length}件中 300件を表示 — 検索・フィルターで絞り込んでください</p>
            )}
            {shown.length === 0 && <p className="px-3 py-6 text-center text-xs text-zinc-400" data-testid="sku-avail-empty">該当するSKUがありません</p>}
          </div>
        </div>
      )}

      {/* 詳細マップ（トグル時のみ） */}
      {!busy && view === 'map' && maps.map((m) => (
        <div key={m.template.id} className="space-y-3" data-testid={`sku-avail-detailmap-${m.template.code}`}>
          <p className="text-xs font-bold text-zinc-700 flex items-center gap-1.5"><MapTrifold size={14} className="text-sky-600" /> {m.template.name}</p>
          {(m.buckets || []).map((b) => (
            <DetailGrid key={b.bucket} bucket={b} map={m} stateBySku={stateBySku} />
          ))}
          {(m.buckets || []).length === 0 && <p className="text-[11px] text-zinc-400">発番なし</p>}
        </div>
      ))}

      {/* 操作履歴 */}
      {view === 'history' && <HistoryPanel />}
      {view === 'counters' && (
        <div data-testid="sku-avail-counters">
          <SkuCountersCard />
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="sku-avail-confirm">
          <DialogHeader>
            <DialogTitle className="text-base">番号を解放する</DialogTitle>
            <DialogDescription className="text-sm text-zinc-600 pt-1">
              選択した予約番号（{selected.size}件）を解放します。
              解放しただけでは再利用されません — 管理者が「再利用可」に変更し、発番時に明示的に選んだときのみ使われます。
              既存の品目・資産・QRラベルは変更されません。よろしいですか？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="sku-avail-confirm-cancel">キャンセル</Button>
            <Button onClick={doRelease} className="bg-amber-600 hover:bg-amber-700" data-testid="sku-avail-confirm-ok">解放する</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveTargets} onOpenChange={(o) => { if (!o) setResolveTargets(null); }}>
        <DialogContent className="max-w-md" data-testid="sku-avail-resolve-confirm">
          <DialogHeader>
            <DialogTitle className="text-base">競合を解消する</DialogTitle>
            <DialogDescription className="text-sm text-zinc-600 pt-1">
              以下の番号は正式に発番されていませんが、実データ側で手動使用されています。
              「発行済（使用中）」として正式登録し、今後の二重発番を防ぎます。
              既存の品目・資産は一切変更されません。
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-44 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50/50 p-2 space-y-1" data-testid="sku-avail-resolve-list">
            {(resolveTargets || []).map((r) => (
              <p key={r.key} className="text-xs font-mono text-zinc-700">
                {r.sku}
                <span className="ml-2 font-sans text-zinc-500">
                  {r.owner_type && `[${r.owner_type}] `}{r.owner_name || '手動使用'}
                </span>
              </p>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResolveTargets(null)} data-testid="sku-avail-resolve-cancel">キャンセル</Button>
            <Button onClick={doResolve} className="bg-amber-600 hover:bg-amber-700" data-testid="sku-avail-resolve-ok">
              発番済みとして登録（{(resolveTargets || []).length}）
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const HIST_ACTIONS = {
  release: { label: '解放', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  approve_reuse: { label: '再利用可に承認', chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  revoke_release: { label: '解放を取消', chip: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  manual_reuse: { label: '管理者が再利用', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  conflict_resolve: { label: '競合解消', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function HistoryPanel() {
  const [items, setItems] = useState([]);
  const [hq, setHq] = useState('');
  const [action, setAction] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/history`, {
        params: { ...(hq.trim() ? { q: hq.trim() } : {}), ...(action !== 'all' ? { action } : {}) },
        withCredentials: true,
      }).then((r) => setItems(r.data?.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [hq, action]);

  return (
    <div className="space-y-2" data-testid="sku-history-panel">
      <div className="flex items-center gap-1.5 flex-wrap">
        {[['all', 'すべて'], ...Object.entries(HIST_ACTIONS).map(([k, v]) => [k, v.label])].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setAction(v)}
            data-testid={`sku-history-action-${v}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              action === v ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-sky-50'}`}>
            {label}
          </button>
        ))}
        <Input value={hq} onChange={(e) => setHq(e.target.value)} placeholder="SKU / 実行者で検索..."
          className="h-8 text-xs flex-1 min-w-[180px]" data-testid="sku-history-search" />
      </div>
      <div className="rounded-xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-xs" data-testid="sku-history-table">
          <thead className="bg-zinc-100 text-zinc-700 text-[10px]">
            <tr>
              <th className="text-left px-2 py-2 font-semibold">日時</th>
              <th className="text-left px-2 py-2 font-semibold">操作</th>
              <th className="text-left px-2 py-2 font-semibold">SKU</th>
              <th className="text-left px-2 py-2 font-semibold">実行者</th>
              <th className="text-left px-2 py-2 font-semibold hidden md:table-cell">詳細</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200" data-testid={`sku-history-row-${r.id}`}>
                <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">{formatJST(r.at)}</td>
                <td className="px-2 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold whitespace-nowrap ${
                    (HIST_ACTIONS[r.action] || {}).chip || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>
                    {r.action_label || r.action}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono font-semibold text-zinc-800">{r.sku}</td>
                <td className="px-2 py-1.5 text-zinc-600">{r.actor_name}</td>
                <td className="px-2 py-1.5 text-zinc-500 hidden md:table-cell">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <p className="px-3 py-6 text-center text-xs text-zinc-400">読み込み中...</p>}
        {!loading && items.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-zinc-400" data-testid="sku-history-empty">
            操作履歴はまだありません — 解放・再利用可への承認・取消・管理者による再利用・競合解消がここに記録されます
          </p>
        )}
      </div>
      <p className="text-[10px] text-zinc-400">※ この履歴は追記専用です。機能導入以前の操作は記録されていません。</p>
    </div>
  );
}

function cellsFor(bucket, map, stateBySku, from, count) {
  const width = map.counter_width || 4;
  const start = map.start || 0;
  const usedBy = new Map(bucket.used.map((u) => [u.serial, u]));
  const conflictSet = new Set((bucket.conflicts || []).map((c) => c.serial));
  const gapSku = new Map((bucket.gaps_detail || []).map((g) => [g.serial, g.sku]));
  const end = Math.max(bucket.max_serial + 5, start + count - 1);
  const out = [];
  for (let n = Math.max(start, from); n <= Math.min(end, from + count - 1); n += 1) {
    const u = usedBy.get(n);
    let kind = 'gap';
    if (u) kind = u.linked ? 'used' : 'issued';
    else if (conflictSet.has(n)) kind = 'conflict';
    else if (stateBySku.get(gapSku.get(n))) kind = stateBySku.get(gapSku.get(n));
    out.push({ n, kind, sku: u?.sku || gapSku.get(n) || String(n).padStart(width, '0'), u });
  }
  return out;
}

function CompactStrip({ map, stateBySku, rangeStart, setRangeStart, full }) {
  const b = map.buckets?.[0];
  if (!b) return null;
  const width = map.counter_width || 4;
  const start = map.start || 0;
  const size = full ? 200 : 100;
  const from = Math.max(start, rangeStart || start);
  const cells = cellsFor(b, map, stateBySku, from, size);
  const maxN = Math.max(b.max_serial + 5, start);
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-3 space-y-2" data-testid="sku-avail-strip">
      <div className="flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
        <span className="font-mono font-bold text-zinc-700">{b.bucket}</span>
        <span>次の発番予定: <span className="font-mono font-bold text-sky-700">{String(b.next_planned).padStart(width, '0')}</span></span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" disabled={from <= start} onClick={() => setRangeStart(Math.max(start, from - size))}
            className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white disabled:opacity-40" data-testid="sku-avail-strip-prev">◀</button>
          <span className="font-mono">{String(from).padStart(width, '0')}–{String(Math.min(from + size - 1, maxN)).padStart(width, '0')}</span>
          <button type="button" disabled={from + size > maxN} onClick={() => setRangeStart(from + size)}
            className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white disabled:opacity-40" data-testid="sku-avail-strip-next">▶</button>
          <button type="button" onClick={() => setRangeStart(Math.max(start, b.next_planned - 10))}
            className="px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700" data-testid="sku-avail-strip-jump">次候補へ</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-[2px]">
        {cells.map((c) => (
          <span key={c.n} title={`${c.sku} — ${STATUS[c.kind].label}${c.u?.issued_by ? `（${c.u.issued_by}）` : ''}`}
            className={`inline-block h-3.5 w-3.5 rounded-[3px] border cursor-default ${STATUS[c.kind].cell}`} />
        ))}
      </div>
    </div>
  );
}

function DetailGrid({ bucket, map, stateBySku }) {
  const width = map.counter_width || 4;
  const start = map.start || 0;
  const cells = cellsFor(bucket, map, stateBySku, start, 500);
  return (
    <div className="space-y-1.5">
      <p className="font-mono text-[11px] font-bold text-zinc-600">{bucket.bucket}
        <span className="ml-2 font-sans font-normal text-zinc-400">次の発番予定: {String(bucket.next_planned).padStart(width, '0')}</span>
      </p>
      <div className="flex flex-wrap gap-[3px]">
        {cells.map((c) => (
          <span key={c.n} title={`${c.sku} — ${STATUS[c.kind].label}`}
            className={`inline-flex items-center justify-center h-6 min-w-[2.6rem] px-1 rounded border font-mono text-[9px] cursor-default ${STATUS[c.kind].cell}`}>
            {String(c.n).padStart(width, '0')}
          </span>
        ))}
      </div>
    </div>
  );
}
